import { useQuery } from '@tanstack/react-query';
import { featureFlagsClient } from '@terra/shared-rest-clients-feature-flags';
import {
  DEFAULT_TENANT_FEATURE_FLAGS,
  type TenantFeatureFlagsResponseDto,
} from '@terra/shared-contracts-feature-flags';
import { getAuthHeaders } from '../../utils/authHeaders';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';

const EVALUATED_FLAGS_QUERY_KEY = 'query.feature-flags.evaluated';

const getTenantFeatureFlags = async ({
  tenantId,
}: {
  tenantId: string;
}): Promise<TenantFeatureFlagsResponseDto | null> => {
  const authHeaders = await getAuthHeaders();
  const response = await featureFlagsClient.getTenantFeatureFlags({
    params: { tenantId },
    overrideClientOptions: {
      baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
    },
    extraHeaders: authHeaders,
  });

  if (response.status === 200) {
    return response.body;
  }
  return null;
};

export const useTenantFeatureFlags = ({
  tenantId,
  enabled = true,
}: {
  tenantId?: string | null;
  enabled?: boolean;
} = {}) => {
  const { data, isLoading, error, refetch, isFetched } = useQuery({
    queryKey: [EVALUATED_FLAGS_QUERY_KEY, tenantId],
    queryFn: () => getTenantFeatureFlags({ tenantId: tenantId ?? '' }),
    enabled: Boolean(tenantId) && enabled,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  return {
    tenantId: data?.tenantId ?? tenantId,
    featureFlags: {
      ...DEFAULT_TENANT_FEATURE_FLAGS,
      ...(data?.featureFlags ?? {}),
    },
    isLoading,
    error,
    refetch,
    isFetched,
  };
};
